import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagsStore } from '@store/featureFlags';
import { useSessionStore } from '@features/auth';

interface TabConfig {
  name: string;
  i18nKey: string;
  path: string;
  matchPaths: string[];
}

const TABS: TabConfig[] = [
  {
    name: 'overview',
    i18nKey: 'dashboard.overview.tabs.overview',
    path: '/dashboard/overview',
    matchPaths: ['/dashboard/overview'],
  },
  {
    name: 'sessions',
    i18nKey: 'dashboard.overview.tabs.sessions',
    path: '/dashboard',
    matchPaths: ['/dashboard'],
  },
  {
    name: 'integrations',
    i18nKey: 'dashboard.overview.tabs.integrations',
    path: '/dashboard/integrations',
    matchPaths: ['/dashboard/integrations'],
  },
];

export default function DashboardTabs (): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSessionStore.getState().currentUser;
  const featureFlags = useFeatureFlagsStore((s) => s.flags);
  const hasIntegrations = featureFlags.hubspot || featureFlags.outreach || featureFlags.salesforce;
  const canSeeTabs = currentUser !== null && currentUser?.isTendrilUser;

  const getTabs = (
    hasIntegrations: boolean,
    canSeeTabs: boolean,
  ): TabConfig[] => {
    if (!hasIntegrations) {
      if (canSeeTabs) {
        return TABS.filter((t) => t.path !== '/dashboard/integrations');
      } 
      
      return TABS.filter((t) => t.path === '/dashboard');
    } 
    
    if (!canSeeTabs) {
      return TABS.filter((t) => t.path !== '/dashboard/overview');
    } 
    
    return TABS;
  };

  const tabs: TabConfig[] = getTabs(hasIntegrations, canSeeTabs);

  const isActive = (tab: TabConfig): boolean => {
    return tab.matchPaths.some((p) => location.pathname === p);
  };

  return (
    <div className="flex flex-col md:flex-row items-center gap-6">
      <h1 className="text-lg font-semibold">{t('dashboard.overview.title')}</h1>
      <nav className="flex items-center gap-1">
        {tabs.map((tab) => {
          const active = isActive(tab);
          return (
            <button
              key={tab.path}
              onClick={() => {
                navigate(tab.path);
              }}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                active
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(tab.i18nKey)}
            </button>
          );
        })}
      </nav>
    </div>
  );
}